import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import IconButton from '@mui/material/IconButton'
import DraggableDialogTitle from './common/DraggableDialogTitle'
import ClearIcon from '../assets/icons/ClearIcon'
import { useSelector } from 'react-redux'
import { selectConnectTradingAccount } from '../selectors/orders'
import { closeConnectAccountDialog } from '../reducer/orders.reducer'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { selectPrimaryTradingAccount } from '../selectors/user'
import { TradingAccount } from '../proto/generated/trading-bridge'
import { BrokerId, generateBrokerLoginUrl, getBrokerId, keyValueArrayFromTradingAccountParams } from '../util/tradingAccounts'
import { connectTradingAccount } from '../services/apis/tradingAccount'
import { setTradingAccounts } from '../reducer/user.reducer'
import commonActions from '../actions/common.actions'
import AnalyticsManager from '../analytics/AnalyticsManager'
import { isShoonyaBackend } from '../util/backendProvider'

type Props = {
    title: string,
    description: string,
    positiveButtonText: string,
    onDialogClose?: Function,
    iconButtonSx?: object,
}

const ConnectTradingAccountDialog: React.FC<Props> = (props) => {
    const openConnectAccountDialog = useSelector(selectConnectTradingAccount)
    const globalPrimaryTradingAccount: TradingAccount | undefined = useSelector(selectPrimaryTradingAccount)
    const tradingAccounts: TradingAccount[] = useSelector((state: any) => state.userStore.tradingAccounts || [])
    const primaryTradingAccount = useMemo(()=>{
        return tradingAccounts?.find(t=>t.accountId===openConnectAccountDialog?.tradingAccountId)??globalPrimaryTradingAccount
    }, [globalPrimaryTradingAccount, tradingAccounts, openConnectAccountDialog?.tradingAccountId])
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const isAlreadyOnAccountPage = /^\/trading-account(\/[^/]*)?$/.test(location.pathname)

    const handleClose = () => {
        if (typeof props.onDialogClose === 'function') props.onDialogClose()
        dispatch(closeConnectAccountDialog())
    }

    const addPaperTradingAccount = useCallback(async () => {
        const result = await connectTradingAccount({
            body: {
                brokerCode: BrokerId.PAPER_TRADING
            },
            dispatch
        })
        AnalyticsManager.logEvent(`Trading Account connected`, { broker: 'Paper Trading' })
        const index = tradingAccounts.findIndex((t) => t.brokerCode === BrokerId.PAPER_TRADING)
        const newTradingAccounts = [...tradingAccounts]
        if (index !== -1) {
            newTradingAccounts[index].primary = true
        } else if (result.tradingAccount) {
            newTradingAccounts.push(result.tradingAccount)
        }
        const updatedAccounts = newTradingAccounts.map((account) => {
            return {
                ...account,
                primary: account.accountId === result.tradingAccount?.accountId
            }
        })
        dispatch(setTradingAccounts(updatedAccounts))
    }, [dispatch, tradingAccounts])

    const handlePositiveButtonClicked = async () => {
        if (openConnectAccountDialog.type === "connect") {
            dispatch(closeConnectAccountDialog())
            void navigate("/trading-account");
        } else {
            if (primaryTradingAccount) {
                const { brokerCode, tradingAccountParameters } = primaryTradingAccount;
                if (brokerCode === 'PAPER_TRADING') {
                    await addPaperTradingAccount();
                } else if (brokerCode === 'DHAN') {
                    void navigate(`/trading-account/${brokerCode}`);
                } else {
                    const loginUrl = await generateBrokerLoginUrl(
                        getBrokerId(brokerCode),
                        keyValueArrayFromTradingAccountParams(tradingAccountParameters)
                    );
                    void window.open(loginUrl, '_self');
                }
            } else {
                console.warn("Primary trading account not available during reconnect.");
            }
        }
        handleClose();
    };

    useEffect(() => {
        if (
            openConnectAccountDialog.openDialog &&
            isAlreadyOnAccountPage &&
            openConnectAccountDialog.type === "connect"
        ) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: "Connect a trading account to place orders.",
                errorType: "info"
            })
            dispatch(closeConnectAccountDialog())
        }
    }, [openConnectAccountDialog.openDialog, openConnectAccountDialog.type, isAlreadyOnAccountPage, dispatch])
    
    if (isShoonyaBackend()) {
        return null
    }

    return openConnectAccountDialog.openDialog ? (
        <Dialog
            open
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DraggableDialogTitle id="alert-dialog-title">
                {props.title}
            </DraggableDialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 12
                }}
            >
                <ClearIcon fontSize={"small"} />
            </IconButton>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {props.description}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handlePositiveButtonClicked}>
                    {props.positiveButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    ) : null
}

export default memo(ConnectTradingAccountDialog);